/**
 * Engagement cadences — guide §6.7, ADR-001 §8.7. View tier: a cell is computed on read from the last log, the
 * booking, the activity's cadence and whether the client is new. The prototype's ENGA / engDue / engCell /
 * engStats / engMakeTasks (docs/prototype.html L3535–3591) are the specification; the contract decides where
 * they differ ("add what is due" uses the `engagement.due_soon_days` window, not the prototype's fixed
 * "tomorrow or sooner"; a never-done cell is labelled "Overdue", not the prototype's sentinel "Overdue 99d").
 * The booking rule IS the prototype's engDue: a booking after last done is the next due date.
 */
import { NOTE_REQUIRED_ACTIVITIES } from '@amplispot/contracts';
import { addDays, daysBetween } from './usage.js';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** `2026-10-21` → `21 Oct` (the prototype's dayLabel). */
export const dayLabel = (iso) => `${Number(iso.slice(8, 10))} ${MONTHS[Number(iso.slice(5, 7)) - 1]}`;
/** ADR §8.7: a client is new when it started this year (2026 for the v1 data; `clients.since`). */
export const isNewClient = (since, today) => since.slice(0, 4) === today.slice(0, 4);
export const cadenceFor = (a, isNew) => isNew && a.new_client_cadence_days != null ? a.new_client_cadence_days : a.cadence_days;
/** The seeded wording of the column heads (prototype ENGA `beat`), used while the cadence is the seeded one. */
const SEEDED_BEAT = {
    wa: [2, 'every 2 days'],
    theme: [60, 'every 1 to 2 months'],
    mis: [30, 'every month, with suggestions'],
};
/** The column head's rhythm in words. */
export function beatOf(key, cadence) {
    const seeded = SEEDED_BEAT[key];
    if (seeded && seeded[0] === cadence)
        return seeded[1];
    if (cadence === 1)
        return 'every day';
    if (cadence === 7)
        return 'every week';
    if (cadence % 30 === 0)
        return cadence === 30 ? 'every month' : `every ${cadence / 30} months`;
    if (cadence % 7 === 0)
        return `every ${cadence / 7} weeks`;
    return `every ${cadence} days`;
}
export const noteRequired = (key) => NOTE_REQUIRED_ACTIVITIES.includes(key);
/**
 * One cell, as the prototype's engDue: never done = overdue with no date, EVEN IF booked (the booking still shows
 * in the sub-label); otherwise `next_due_on = booked_for` when a booking is after `last_done_on` (earlier or later
 * than the cadence would give), else `last_done_on + cadence`. A booking on or before last done is spent and
 * ignored. Red when `next_due_on` is before today; amber when today or within `dueSoonDays`; green otherwise.
 */
export function engagementCell(c, today, dueSoonDays) {
    const booking = c.booked_for && (c.last_done_on == null || c.booked_for > c.last_done_on) ? c.booked_for : null;
    const next = c.last_done_on == null ? null : (booking ?? addDays(c.last_done_on, c.cadence_days));
    const days = next == null ? null : daysBetween(today, next);
    const state = days == null || days < 0 ? 'overdue' : days <= dueSoonDays ? 'due_soon' : 'on_track';
    const label = days == null ? 'Overdue' : days < 0 ? `Overdue ${-days}d` : days === 0 ? 'Due today' : days <= dueSoonDays ? `In ${days}d` : 'On track';
    const ago = c.last_done_on == null ? null : daysBetween(c.last_done_on, today);
    const sub = (ago == null ? 'Never done' : ago === 0 ? 'Done today' : `Done ${ago}d ago`) + (booking ? `, booked ${dayLabel(booking)}` : '');
    return { cadence_days: c.cadence_days, last_done_on: c.last_done_on, booked_for: booking, next_due_on: next, state, days, label, sub_label: sub };
}
export function countCells(cells) {
    const overdue = cells.filter((c) => c.state === 'overdue').length;
    return {
        total: cells.length,
        overdue,
        due_soon: cells.filter((c) => c.state === 'due_soon').length,
        due_today: cells.filter((c) => c.days === 0).length,
        on_track: cells.length - overdue,
    };
}
/** "Add what is due": the task for one due cell (prototype engMakeTasks; ADR §8.7). */
export function engagementTask(activityName, clientName, cell, today) {
    const due = cell.next_due_on && cell.next_due_on > today ? cell.next_due_on : today;
    const overdueBy = cell.days == null ? Infinity : -cell.days;
    return { title: `${activityName}, ${clientName}`, due, priority: overdueBy > 3 ? 'high' : 'medium' };
}
/** The origin id that keeps "add what is due" to one open task per cell (tasks_engage_open_uniq). */
export const engagementOriginId = (clientId, key) => `${clientId}:${key}`;
//# sourceMappingURL=engagement.js.map